import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Adjust client path if needed
import { PortableText } from '@portabletext/react';

// Define portableTextComponents (Adapt from Kindergarten/PreK)
const portableTextComponents = {
  list: {
    bullet: ({children}) => <ul className="list-disc pl-5 mb-4 space-y-2 text-gray-700">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-5 mb-4 space-y-2 text-gray-700">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li className="flex items-start"><span className="w-2 h-2 bg-[#691e31] rounded-full mr-2 mt-[0.6em] flex-shrink-0"></span><span className="flex-1">{children}</span></li>,
    number: ({children}) => <li className="ml-2">{children}</li>, 
  },
  block: {
    normal: ({children}) => <p className="mb-2 text-gray-700">{children}</p>,
    // Handle H4 style used for subheadings like Eureka Math, HMH Into Reading
    h4: ({children}) => <h4 className="font-medium mb-2 text-[#691e31]">{children}</h4>,
  },
  marks: {
    strong: ({children}) => <strong className="font-medium text-gray-900">{children}</strong>,
    em: ({children}) => <em className="text-sm italic text-gray-600">{children}</em>,
  }
};

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600">{message || 'Error loading page content.'}</p>
  </div>
);

const FirstGrade = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null); 

  useEffect(() => {
    const query = `*[_type == "firstGradePage" && _id == "firstGradePageContent"][0]{
      ...,
      bannerImage { asset->{ _id, url } },
      tabs[]{
        _key,
        tabTitle,
        tabIdentifier { current },
        contentSections[]{
          _key,
          title,
          body
        }
      },
      newsletterTitle,
      newsletterSubtitle,
      teacherName,
      newsletterContent
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
          if (data.tabs && data.tabs.length > 0) {
            setActiveTab(data.tabs[0].tabIdentifier.current);
          }
        } else {
          setError('First Grade Page content not found.');
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching First Grade page data:', err);
        setError('Failed to fetch page content.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !pageData) {
    return <ErrorMessage message={error || 'Page data is unavailable.'} />;
  }

  // Destructure page data
  const {
    bannerImage,
    bannerTitle = 'First Grade',
    tabs = [],
    newsletterTitle = 'Monthly Newsletter',
    newsletterSubtitle = 'Newsletter',
    teacherName = 'Teacher',
    newsletterContent = []
  } = pageData;

  // Find the content for the active tab
  const activeTabData = tabs.find(tab => tab.tabIdentifier.current === activeTab);

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {bannerImage?.asset?.url ? (
            <img
              src={urlFor(bannerImage).width(1200).url()}
              alt={bannerTitle || 'First Grade Banner'}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {bannerTitle}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs - Updated Styling to match Kindergarten */}
            {tabs.length > 0 && (
              <div className="flex overflow-x-auto pb-2 mb-8">
                {tabs.map((tab) => (
                  <button
                    key={tab._key}
                    className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                      activeTab === tab.tabIdentifier.current
                        ? 'bg-[#691e31] text-white shadow-md'
                        : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                    }`}
                    onClick={() => setActiveTab(tab.tabIdentifier.current)}
                  >
                    {tab.tabTitle}
                  </button>
                ))}
                
                {/* Newsletter Tab */}
                <button
                  className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                    activeTab === 'newsletter'
                      ? 'bg-[#691e31] text-white shadow-md'
                      : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                  }`}
                  onClick={() => setActiveTab('newsletter')}
                >
                  {newsletterTitle}
                </button>
              </div>
            )}

                        {/* Tab Content */}
            {activeTabData && activeTab !== 'newsletter' && (
              <div className="space-y-6">
                {activeTabData.contentSections?.map((section) => (
                  <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                    <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                      {section.title}
                    </h3>
                    {section.body && (
                      <PortableText
                        value={section.body}
                        components={portableTextComponents}
                      />
                    )}
                </section>
                ))}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">{newsletterSubtitle}</h3>
                    <p className="text-white/80 text-sm">First Grade Class • {teacherName}</p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    {newsletterContent && newsletterContent.length > 0 && (
                      <PortableText
                        value={newsletterContent}
                        components={portableTextComponents}
                      />
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FirstGrade; 